import moment from "moment";

/*
 * Customer-first export file names (SMT-1454):
 *   {Customer} - {Export type} - {Period}.{extension}
 * e.g. "Muster GmbH - SuSa - 2025-01 bis 2025-06.pdf".
 *
 * Mirrors buildExportFileName in kontax-backend (core/common/utilities/file.ts)
 * rule for rule. Keep the two in step.
 */

export interface ExportFileNameUser {
  firstName?: string | null;
  lastName?: string | null;
  businessTradingName?: string | null;
  companyType?: string | null;
  email?: string | null;
}

type YearLike = number | string;

/*
 * What an export covers. Dates are read as UTC calendar days; pass
 * "YYYY-MM-DD" strings when the day is already known.
 * - year: the whole calendar year -> "2025"
 * - month: one month -> "2025-03"
 * - quarter: "Q1".."Q4" -> "2025-Q3"
 * - kontax: a Kontax period value ("01".."12", "Q1".."Q4", "YEAR") plus year
 * - monthRange: first to last month (inclusive); one month or Jan-Dec collapse
 * - dateRange: first to last day (inclusive); exactly Jan 1 - Dec 31 of one
 *   year collapses to the year, everything else stays a day range
 */
export type ExportFilePeriod =
  | { kind: "year"; year: YearLike }
  | { kind: "month"; year: YearLike; month: number | string }
  | {
      kind: "quarter";
      year: YearLike;
      quarter: "Q1" | "Q2" | "Q3" | "Q4";
    }
  | { kind: "kontax"; year: YearLike; period: string }
  | {
      kind: "monthRange";
      from: { year: YearLike; month: number | string };
      to: { year: YearLike; month: number | string };
    }
  | {
      kind: "dateRange";
      from: Date | string;
      to: Date | string;
    };

/* Same list as isCompany in kontax-backend (modules/common/user/utilities/isCompany.ts). */
const COMPANY_TYPES = [
  "Ltd.",
  "e. K.",
  "Partnerschaft (nach PartGG)",
  "GbR",
  "OHG",
  "KG",
  "KGaA",
  "GmbH",
  "GmbH und Co. KG",
  "UG (haftungsbeschränkt)",
];

export const isCompanyType = (companyType?: string | null): boolean =>
  !!companyType && COMPANY_TYPES.includes(companyType);

const TRANSLITERATION: Record<string, string> = {
  ä: "ae",
  ö: "oe",
  ü: "ue",
  Ä: "Ae",
  Ö: "Oe",
  Ü: "Ue",
  ß: "ss",
};

/* German umlauts and ß explicitly, any other accent by dropping the mark (é -> e). */
export const transliterate = (text: string): string =>
  text
    .replace(/[äöüÄÖÜß]/g, (char) => TRANSLITERATION[char])
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

const pad2 = (value: number | string) => String(value).padStart(2, "0");

const formatMonth = (year: YearLike, month: number | string) =>
  `${year}-${pad2(month)}`;

const formatExportPeriod = (period: ExportFilePeriod): string => {
  switch (period.kind) {
    case "year":
      return String(period.year);
    case "month":
      return formatMonth(period.year, period.month);
    case "quarter":
      return `${period.year}-${period.quarter}`;
    case "kontax": {
      const value = String(period.period).toUpperCase();
      if (value === "YEAR") {
        return String(period.year);
      }
      return /^Q[1-4]$/.test(value)
        ? `${period.year}-${value}`
        : formatMonth(period.year, value);
    }
    case "monthRange": {
      const { from, to } = period;
      const first = formatMonth(from.year, from.month);
      const last = formatMonth(to.year, to.month);
      if (first === last) {
        return first;
      }
      return Number(from.month) === 1 &&
        Number(to.month) === 12 &&
        String(from.year) === String(to.year)
        ? String(from.year)
        : `${first} bis ${last}`;
    }
    case "dateRange": {
      const from = moment.utc(period.from);
      const to = moment.utc(period.to);
      const isFullYear =
        from.year() === to.year() &&
        from.format("MM-DD") === "01-01" &&
        to.format("MM-DD") === "12-31";
      return isFullYear
        ? String(from.year())
        : `${from.format("YYYY-MM-DD")} bis ${to.format("YYYY-MM-DD")}`;
    }
  }
};

/*
 * Period for a "YYYY-MM-DD" start and end: whole months become a month range
 * (one month, or Jan-Dec as the year), anything else a day range.
 */
export const periodFromIsoDates = (
  from: string,
  to: string
): ExportFilePeriod => {
  const start = moment.utc(from, "YYYY-MM-DD");
  const end = moment.utc(to, "YYYY-MM-DD");
  const isWholeMonths =
    start.date() === 1 && end.isSame(end.clone().endOf("month"), "day");

  return isWholeMonths
    ? {
        kind: "monthRange",
        from: { year: start.year(), month: start.month() + 1 },
        to: { year: end.year(), month: end.month() + 1 },
      }
    : { kind: "dateRange", from, to };
};

/*
 * Company -> Firmenname as typed; anyone else -> "Firstname Lastname". Falls
 * back to the other form, then the email.
 */
const getExportCustomerName = (user: ExportFileNameUser): string => {
  const person = [user.firstName, user.lastName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");
  const company = (user.businessTradingName || "").trim();
  const ordered = isCompanyType(user.companyType)
    ? [company, person]
    : [person, company];

  return ordered.find(Boolean) || (user.email || "").trim();
};

/* Umlauts transliterated, then drop what a file system rejects. */
const cleanFileNamePart = (text: string): string =>
  transliterate(text)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[. ]+$/, "");

export const buildExportFileName = ({
  user,
  exportType,
  period,
  extension,
}: {
  user: ExportFileNameUser;
  exportType: string;
  period?: ExportFilePeriod;
  extension: string;
}): string => {
  const base = [
    getExportCustomerName(user),
    exportType,
    period ? formatExportPeriod(period) : "",
  ]
    .map(cleanFileNamePart)
    .filter(Boolean)
    .join(" - ");

  return `${base}.${extension.replace(/^\./, "")}`;
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/*
 * Reads the file name from a Content-Disposition header value. Prefers
 * `filename*=UTF-8''...`, then a quoted `filename="..."`, then an unquoted
 * `filename=...` (older backends). Returns undefined when there is none.
 */
export const getFileNameFromContentDisposition = (
  header?: string | null
): string | undefined => {
  if (!header) {
    return undefined;
  }

  const extended = header.match(/filename\*\s*=\s*([^;]+)/i);
  if (extended) {
    const raw = extended[1].trim().replace(/^"|"$/g, "");
    const encoded = raw.replace(/^[^']*'[^']*'/, "");
    const decoded = safeDecode(encoded).trim();
    if (decoded) {
      return decoded;
    }
  }

  const quoted = header.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"/i);
  if (quoted) {
    const value = quoted[1].replace(/\\(.)/g, "$1").trim();
    if (value) {
      return value;
    }
  }

  const unquoted = header.match(/filename\s*=\s*([^;]+)/i);
  if (unquoted) {
    const value = unquoted[1].trim();
    if (value) {
      return value;
    }
  }

  return undefined;
};
